/**
 * Asset One-Pager Modal
 *
 * Asset-scoped variant of the org-wide `EmissionsOnePagerModal`. Reuses
 * `EmissionsOnePagerPreview` (the visual template) but feeds it data
 * derived purely from this asset's `AssetComparisonItem` and per-month
 * series. Year selector + format toggle (PDF/Image) + download button
 * mirror the org modal so the UX is consistent.
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter } from 'reactstrap';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { useAppSelector } from '@reducers/index';
import { currentInitiative } from '@selectors/initiative';
import {
  useGetAssetComparisonQuery,
  useGetAssetByMonthQuery,
  useGetSettingsQuery,
} from '../../api/emissions';
import {
  getCompletedFiscalYear,
  getFiscalYearOptions,
  getFiscalYearPeriod,
  getPreviousYearPeriod,
} from '../FiscalYearSelector';
import {
  EmissionsOnePagerPreview,
  type OnePagerMode,
} from '../../features/one-pager/EmissionsOnePagerPreview';
import { useGenerateOnePager } from '../../features/one-pager/useGenerateOnePager';
import {
  EmissionScope,
  EmissionUnit,
  type ActivityType,
  type ActivityTypeReport,
  type AssetComparisonItem,
  type EmissionAsset,
  type EmissionSummary,
} from '../../types/emissions';
import '../../features/one-pager/one-pager-modal.scss';

interface AssetOnePagerModalProps {
  isOpen: boolean;
  toggle: () => void;
  initiativeId: string;
  asset: EmissionAsset;
}

const FISCAL_YEAR_OPTIONS = getFiscalYearOptions();

const FORMAT_OPTIONS: ReadonlyArray<{ value: OnePagerMode; label: string; icon: string }> = [
  { value: 'pdf', label: 'PDF (A4)', icon: 'fa-file-pdf' },
  { value: 'image', label: 'Image (1080×1080)', icon: 'fa-image' },
];

const PREVIEW_MAX_SCALE = 0.55;
const PREVIEW_FRAME_PADDING = 32;
const PREVIEW_DIMENSIONS: Record<OnePagerMode, { width: number; height: number }> = {
  pdf: { width: 800, height: 1131 },
  image: { width: 1080, height: 1080 },
};

const ErrorAlert = ({ className, children }: { className?: string; children: ReactNode }) => (
  <div className={`one-pager-modal__alert alert alert-danger py-2 ${className ?? ''}`} role='alert'>
    <i className='fa fa-exclamation-circle me-2' />
    {children}
  </div>
);

/**
 * Project the asset's comparison row into the org-wide `EmissionSummary` shape
 * the preview expects. Per-scope and totals come straight from the API; gas
 * breakdowns are unavailable at the asset level (the comparison endpoint
 * doesn't return them) so the preview's gas blocks just won't render — that's
 * expected and the layout handles it.
 */
const toEmissionSummary = (item: AssetComparisonItem | undefined): EmissionSummary | undefined => {
  if (!item) return undefined;
  const byScope: Record<string, number> = {};
  for (const s of item.scopes) byScope[s.scope] = s.co2e;
  return {
    totalCO2e: item.totalCo2e,
    unit: EmissionUnit.KgCO2e,
    totalTonnesCO2e: item.totalCo2e / 1000,
    byScope,
    activityCount: item.totalActivities,
  };
};

/** Map asset `activityTypes` into the `ActivityTypeReport` shape used by the hotspots block. */
const toActivityTypeReports = (item: AssetComparisonItem | undefined): ActivityTypeReport[] => {
  if (!item) return [];
  return item.activityTypes.map((a) => {
    // Find the scope that contains this activity type's emissions; fall back
    // to Scope1 since the report is purely for visual ranking.
    const scope = item.scopes.find((s) => s.co2e > 0)?.scope as EmissionScope | undefined;
    return {
      // Why: `AssetComparisonItem.activityTypes[].activityType` is typed as `string`
      // by the API surface, but the server only ever emits values from the
      // `ActivityType` union (Scope1ActivityType | Scope2ActivityType). The cast
      // narrows to that union for the preview component.
      activityType: a.activityType as ActivityType,
      scope: scope ?? EmissionScope.Scope1,
      total: a.co2e,
      count: a.count,
    };
  });
};

export const AssetOnePagerModal = ({ isOpen, toggle, initiativeId, asset }: AssetOnePagerModalProps) => {
  const initiative = useAppSelector(currentInitiative);
  const [selectedYear, setSelectedYear] = useState<number>(getCompletedFiscalYear());
  const [mode, setMode] = useState<OnePagerMode>('pdf');

  const period = getFiscalYearPeriod(selectedYear);
  const priorPeriod = getPreviousYearPeriod(selectedYear);
  const skip = !isOpen || !initiativeId || !asset._id;

  const comparisonQuery = useGetAssetComparisonQuery(
    { initiativeId, periodStart: period.start, periodEnd: period.end },
    { skip },
  );
  const priorComparisonQuery = useGetAssetComparisonQuery(
    { initiativeId, periodStart: priorPeriod.start, periodEnd: priorPeriod.end },
    { skip },
  );
  const monthlyQuery = useGetAssetByMonthQuery(
    { initiativeId, assetId: asset._id, periodStart: period.start, periodEnd: period.end },
    { skip },
  );
  const settingsQuery = useGetSettingsQuery({ initiativeId }, { skip });

  const assetComparison = useMemo(
    () => comparisonQuery.data?.assets?.find((a) => a._id === asset._id),
    [comparisonQuery.data, asset._id],
  );
  const priorAssetComparison = useMemo(
    () => priorComparisonQuery.data?.assets?.find((a) => a._id === asset._id),
    [priorComparisonQuery.data, asset._id],
  );

  const summary = useMemo(() => toEmissionSummary(assetComparison), [assetComparison]);
  const priorSummary = useMemo(() => toEmissionSummary(priorAssetComparison), [priorAssetComparison]);
  const hotspots = useMemo(() => toActivityTypeReports(assetComparison), [assetComparison]);

  const hasBlockingError = comparisonQuery.isError || settingsQuery.isError;
  const isAnyFetching =
    comparisonQuery.isFetching ||
    priorComparisonQuery.isFetching ||
    monthlyQuery.isFetching ||
    settingsQuery.isFetching;
  const isReady = Boolean(summary && !hasBlockingError && !isAnyFetching);

  const captureRef = useRef<HTMLDivElement>(null);
  const { generate, clearError, isExporting, error } = useGenerateOnePager();

  const [frameEl, setFrameEl] = useState<HTMLDivElement | null>(null);
  const [frameWidth, setFrameWidth] = useState(0);
  useEffect(() => {
    if (!frameEl) return;
    const observer = new ResizeObserver(([entry]) => {
      if (entry.contentRect.width > 0) setFrameWidth(entry.contentRect.width);
    });
    observer.observe(frameEl);
    return () => observer.disconnect();
  }, [frameEl]);

  const [generatedAt] = useState<Date>(() => new Date());

  const handleYearChange = (year: number) => {
    setSelectedYear(year);
    clearError();
  };
  const handleModeChange = (next: OnePagerMode) => {
    setMode(next);
    clearError();
  };

  const exportName = `${initiative?.name ?? 'Initiative'} · ${asset.name}`;

  const handleDownload = async () => {
    if (!captureRef.current || !summary) return;
    await generate({
      mode,
      node: captureRef.current,
      initiativeId,
      initiativeName: exportName,
      period,
    });
  };

  const previewSize = PREVIEW_DIMENSIONS[mode];
  const availableWidth = Math.max(0, frameWidth - PREVIEW_FRAME_PADDING);
  const previewScale = frameWidth > 0
    ? Math.min(PREVIEW_MAX_SCALE, availableWidth / previewSize.width)
    : PREVIEW_MAX_SCALE;

  const previewProps = summary && {
    mode,
    initiativeName: exportName,
    initiativeLogo: initiative?.profile,
    period,
    summary,
    priorSummary,
    hotspots,
    intensity: undefined,
    settings: settingsQuery.data,
    monthly: monthlyQuery.data,
    targets: undefined,
    generatedAt,
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size='lg'>
      <ModalHeader toggle={toggle}>Export Asset One-Pager</ModalHeader>
      <ModalBody>
        <p className='text-ThemeTextLight mb-3'>
          A media-kit one-pager summarising this asset&apos;s carbon footprint for the selected reporting year.
        </p>

        <div className='d-flex gap-3 mb-3 align-items-end'>
          <div style={{ flex: '0 0 140px' }}>
            <label className='form-label fw-medium small' htmlFor='asset-one-pager-year'>
              Reporting year
            </label>
            <select
              id='asset-one-pager-year'
              className='form-select form-select-sm'
              value={selectedYear}
              onChange={(e) => handleYearChange(Number(e.target.value))}
            >
              {FISCAL_YEAR_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>
          <div className='flex-grow-1'>
            <label className='form-label fw-medium small'>Format</label>
            <div className='btn-group w-100'>
              {FORMAT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type='button'
                  className={`btn btn-sm ${mode === opt.value ? 'btn-success' : 'btn-outline-secondary'}`}
                  onClick={() => handleModeChange(opt.value)}
                >
                  <i className={`fa ${opt.icon} me-1`} />
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {hasBlockingError ? (
          <ErrorAlert className='mb-3'>
            We couldn&apos;t load the asset&apos;s emissions data. Please close this dialog and try again.
          </ErrorAlert>
        ) : null}

        <div
          ref={setFrameEl}
          className='one-pager-modal__preview-frame'
          style={{ height: previewSize.height * previewScale + PREVIEW_FRAME_PADDING }}
        >
          {previewProps ? (
            <div
              className='one-pager-modal__preview-scaled'
              style={{
                width: previewSize.width,
                height: previewSize.height,
                transform: `scale(${previewScale})`,
              }}
              aria-hidden='true'
            >
              <EmissionsOnePagerPreview {...previewProps} />
            </div>
          ) : (
            <div className='text-ThemeTextLight' aria-busy='true' role='status'>
              {hasBlockingError ? 'Unable to load preview.' : 'Loading preview…'}
            </div>
          )}
        </div>

        {/* Off-screen unscaled copy: html2canvas captures this one so the scale
            transform on the visible copy can't affect layout measurement. */}
        {previewProps ? (
          <div className='one-pager-modal__capture' aria-hidden='true'>
            <EmissionsOnePagerPreview ref={captureRef} {...previewProps} />
          </div>
        ) : null}

        {error ? <ErrorAlert className='mt-3 mb-0'>{error}</ErrorAlert> : null}
      </ModalBody>
      <ModalFooter>
        <Button variant='secondary' onClick={toggle} disabled={isExporting}>
          Cancel
        </Button>
        <Button onClick={handleDownload} disabled={isExporting || !isReady}>
          {isExporting
            ? <><span className='spinner-border spinner-border-sm me-1' />Generating…</>
            : <><i className='fa fa-download me-1' />Download {mode === 'pdf' ? 'PDF' : 'image'}</>}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
